import { useMemo } from 'react';

import {
	createResource,
	useResource,
	type RouterContext,
	type RouteResource,
} from '@atlassian/react-resource-router';
import { getIssueKey } from '@jira/router-resources__utils/src/getIssueKey.tsx';

type JsmChatOpsEntitlement = {
	chatOpsEntitlementEnabled: boolean | undefined;
};

export const jsmChatOpsEntitlementResource: RouteResource<JsmChatOpsEntitlement | undefined> =
	createResource<JsmChatOpsEntitlement | undefined>({
		type: 'JSM_CHATOPS_ENTITLEMENT',
		getKey: ({ match, query }: RouterContext) => {
			const issueKey = getIssueKey(match);

			if (!issueKey) {
				if (query && query.selectedIssue) {
					return query.selectedIssue || '';
				}
			}

			return issueKey || '';
		},
		getDataLoader: () =>
			import(
				/* webpackChunkName: "async-resource-jsm-chatops", jiraSpaEntry: "async-resource-jsm-chatops" */
				'./getData.tsx'
			),
		maxAge: __SERVER__ ? 0 : Number.MAX_SAFE_INTEGER,
		isBrowserOnly: false,
	});

/**
 * Returns chatOpsEntitlementEnabled from the route resource (SSR-safe when resource is in route).
 * When the resource is not in the route (e.g. flag off), data is undefined and callers should
 * fall back to client-only store/useEffect.
 */
export function useJsmChatOpsEntitlementResource(): JsmChatOpsEntitlement & {
	refresh: () => void;
} {
	const { data, refresh } = useResource(jsmChatOpsEntitlementResource);

	return useMemo(() => {
		return {
			chatOpsEntitlementEnabled: data?.chatOpsEntitlementEnabled ?? undefined,
			refresh,
		};
	}, [data, refresh]);
}
